import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { clicksAPI } from '../../lib/api';
import { motion } from 'framer-motion';
import { Shield, Truck, Clock, CreditCard } from 'lucide-react';

const ProductCard = ({ product, settings, isFeatured }) => {
  const [timeLeft, setTimeLeft] = useState(settings?.deal_timer_minutes * 60 || 600);

  useEffect(() => {
    if (!isFeatured) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : settings?.deal_timer_minutes * 60 || 600));
    }, 1000);
    return () => clearInterval(timer);
  }, [isFeatured, settings?.deal_timer_minutes]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const cardClasses = isFeatured
    ? 'relative rounded-3xl p-6 md:p-8 flex flex-col overflow-hidden transform md:scale-105 z-10'
    : 'bg-white rounded-3xl border border-slate-200 shadow-lg p-6 md:p-7 flex flex-col card-hover';

  const cardStyle = isFeatured
    ? {
        background: 'linear-gradient(135deg, #f0fdf4, #dcfce7, #bbf7d0)',
        border: `3px solid ${settings?.accent_color || '#dc2626'}`,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      }
    : {};

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={cardClasses}
      style={cardStyle}
      data-testid={`product-card-${product.bottles}`}
    >
      {isFeatured && (
        <>
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-teal-300/30" />
          <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-emerald-300/30" />
          <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
            BEST VALUE
          </div>
        </>
      )}

      <div className="text-center relative">
        <h3
          className={`font-heading text-3xl font-bold ${isFeatured ? 'text-red-600' : 'text-slate-900'}`}
        >
          {product.title}
        </h3>
        <p className={`mt-1 text-sm font-semibold ${isFeatured ? 'text-red-600' : 'text-slate-600'}`}>
          {product.subtitle}
        </p>

        <div className="mt-5 flex justify-center">
          <img
            src={product.image_url || 'https://placehold.co/200x240/1e3a8a/ffffff?text=Product'}
            alt={product.title}
            className={`${isFeatured ? 'h-44' : 'h-36'} w-auto drop-shadow-xl animate-float`}
          />
        </div>

        <div className="mt-5 flex items-end justify-center gap-2">
          <span
            className={`font-heading ${isFeatured ? 'text-7xl' : 'text-6xl'} font-black`}
            style={{ color: isFeatured ? settings?.accent_color : settings?.primary_color }}
          >
            ${product.price}
          </span>
          <span className={`text-lg font-bold mb-2 ${isFeatured ? 'text-red-600' : 'text-slate-700'}`}>
            /Bottle
          </span>
        </div>

        <div className="mt-4 space-y-2">
          <div
            className={`inline-flex items-center justify-center rounded-lg px-4 py-2 font-bold text-sm ${
              isFeatured ? 'bg-yellow-300' : 'bg-slate-100'
            }`}
          >
            {product.savings}
          </div>
          <div
            className={`inline-flex items-center justify-center rounded-lg px-4 py-2 font-bold text-sm ${
              isFeatured ? 'bg-emerald-300' : 'bg-slate-100'
            }`}
          >
            {product.guarantee}
          </div>
          {product.bonus_text && (
            <div className="inline-flex items-center justify-center rounded-lg bg-cyan-200 px-4 py-2 font-bold text-sm">
              {product.bonus_text}
            </div>
          )}
        </div>
      </div>

      <a
        href={product.buy_link || '#checkout'}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => { fetch('/api/clicks/track', { method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({ product_name: product.title, bottles: product.bottles, source: 'buy_button' }), keepalive: true }); }}
        className="mt-6 rounded-2xl btn-cta font-heading font-bold text-xl md:text-2xl py-5 text-center shadow-xl animate-pulse-glow relative"
        data-testid={`buy-button-${product.bottles}`}
      >
        BUY NOW
      </a>

      {isFeatured && (
        <div
          className="mt-4 text-center font-bold text-lg tracking-wider"
          style={{ color: settings?.accent_color }}
          data-testid="deal-timer"
        >
          {formatTime(timeLeft)}
        </div>
      )}

      <div className="mt-4 text-center text-xs text-slate-500 font-semibold">
        <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
          <span className="pill">VISA</span>
          <span className="pill">Mastercard</span>
          <span className="pill">AmEx</span>
          <span className="pill">PayPal</span>
        </div>
      </div>

      <div className="mt-5 text-center text-sm font-bold">
        <span className="text-slate-600">TOTAL:</span>
        <span className="strike-price ml-2">${product.original_price}</span>
        <span className="ml-2 text-slate-900">${product.price * product.bottles}</span>
        <div className="mt-1 text-slate-700">
          {product.free_shipping ? 'FREE US SHIPPING' : '+ SHIPPING'}
        </div>
      </div>

      <div className="mt-5 text-sm text-slate-600">
        <ul className="space-y-2">
          {product.features?.map((feature, idx) => (
            <li key={idx} className="flex gap-2">
              <span
                className="mt-1.5 h-2 w-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: isFeatured ? settings?.secondary_color : settings?.primary_color }}
              />
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
};

export const PricingSection = () => {
  const { settings, products, contentSections } = useSite();

  if (!products?.length) return null;

  // Sort products: featured first (6 bottles), then others by order
  const sortedProducts = [...products].sort((a, b) => {
    if (a.is_featured && !b.is_featured) return 0;
    if (!a.is_featured && b.is_featured) return 0;
    return a.order - b.order;
  });

  return (
    <section className="mx-auto max-w-7xl px-4 pb-16" id="order">
      <div className="rounded-3xl bg-gradient-to-b from-slate-50 to-white border border-slate-200 shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-6 md:px-10 pt-10 pb-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-slate-200 text-xs md:text-sm font-bold text-slate-800">
            <span className="inline-block w-2 h-2 rounded-full bg-red-600 animate-pulse" />
            Limited-time offer • 60-day guarantee • Secure checkout
          </div>
          <h2 className="mt-5 font-heading text-3xl md:text-5xl font-black leading-tight text-slate-900">
            {contentSections?.hero_title?.title || 'Choose Your Package'}
          </h2>
          <p className="mt-4 text-slate-600 text-base md:text-lg max-w-3xl mx-auto font-display italic">
            {contentSections?.hero_title?.content ||
              'Pick the plan that matches your goals. Bigger bundles unlock better savings.'}
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="px-6 md:px-10 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
            {sortedProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                settings={settings}
                isFeatured={product.is_featured}
              />
            ))}
          </div>

          {/* Trust Badges */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl border border-slate-200 p-5 bg-white shadow-lg flex gap-4 items-start"
            >
              <Shield className="w-8 h-8 text-green-600 flex-shrink-0" />
              <div>
                <div className="font-heading font-bold">60-Day Guarantee</div>
                <div className="mt-1 text-sm text-slate-600">
                  Try with confidence. Follow the refund policy within the guarantee window.
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="rounded-2xl border border-slate-200 p-5 bg-white shadow-lg flex gap-4 items-start"
            >
              <CreditCard className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <div className="font-heading font-bold">Secure Checkout</div>
                <div className="mt-1 text-sm text-slate-600">
                  Encrypted payment processing and trusted payment options.
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl border border-slate-200 p-5 bg-white shadow-lg flex gap-4 items-start"
            >
              <Truck className="w-8 h-8 text-purple-600 flex-shrink-0" />
              <div>
                <div className="font-heading font-bold">Ships Fast</div>
                <div className="mt-1 text-sm text-slate-600">
                  Free US shipping on select packages. Delivery varies by region.
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
